import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    NgZone,
    Signal,
    viewChild
} from '@angular/core';
import {SelectComponent, SelectOption} from "../../form-elements/select/select/select.component";
import {LamelareaStore, LamellaAreaStore} from "./lamelarea.store";
import {LeadingZeroPipe} from "../../pipe/leading-zero.pipe";
import {LamellaSetComponent} from "./components/lamella-set/lamella-set/lamella-set.component";
import {CardWrapComponent} from "../../card-wrap/card-wrap/card-wrap.component";
import {CardElementComponent} from "../../card-wrap/card-element/card-element/card-element.component";
import {ToggleComponent} from "../../toggle/toggle/toggle.component";
import {InputComponent} from "../../form-elements/input/input/input.component";
import {ButtonGroupComponent} from "../../form-elements/button-group/button-group.component";
import {ViewPortComponent} from "../../../threejs/components/view-port/view-port.component";
import {AssemblyConfiguratorComponent} from "../../../assembly-configurator/assembly-configurator.component";
import {ColorSelectorComponent} from "../../modals/color-selector/color-selector/color-selector.component";
import {ColorConverter, ColorInfo} from "../../../core/util/color-converter";
import {LamelareaImpl} from './lamelarea.class';
import {translate} from '../../translate/translate';
import {TranslatePipe} from "@ngx-translate/core";
import {FrameProfileFactory} from "./profiles/frame-profile/frame-profile.factory";
import {FrameProfileType} from "./profiles/frame-profile/frame-profile";
import {RenderingService} from "../../../threejs/service/rendering.service";


@Component({
    selector: 'app-product-card',
    standalone: true,
    imports: [
        LeadingZeroPipe,
        LamellaSetComponent,
        SelectComponent,
        CardWrapComponent,
        CardElementComponent,
        ToggleComponent,
        InputComponent,
        ButtonGroupComponent,
        ViewPortComponent,
        AssemblyConfiguratorComponent,
        ColorSelectorComponent,
        TranslatePipe
    ],
    templateUrl: './product-card.component.html',
    styleUrl: './product-card.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,

})
export class ProductCardComponent implements AfterViewInit{
    readonly renderingService = inject(RenderingService);
    readonly lamelareaStore: LamellaAreaStore = inject(LamelareaStore);
    readonly ngZone = inject(NgZone);

    /**
     * wrapper element observer
     */
    private readonly cardElement: Signal<ElementRef> = viewChild('card');

    readonly hasCoverProfile = computed(() => this.lamelarea().coverProfile !== null);

    /**
     * viewport component observer
     */
    private readonly viewPortComponent = viewChild(ViewPortComponent);

    /**
     * **ID** of the lamella area
     * @typeParam **ID** {string}
     */
    readonly lamelareaId = input.required<string>();

    /**
     * store provided lamella area
     * @typeParam {LamelareaImpl}
     */
    readonly lamelarea: Signal<LamelareaImpl> = computed(() => this.lamelareaStore.getLamelarea(this.lamelareaId()));

    /**
     * Mode compare key of the assembly configurator since toggle returns translated value
     */
    readonly modeKey = translate('product-card.toggle.auto');

    readonly frameSelections: Array<FrameProfileType> = ['no frame profile', 'frame profile 50x70', 'frame profile 70x78'];

    readonly surfaceSelections: Array<string> = [ "Powder Coated", "Anodized" ];

    protected readonly isLastLamellaArea = computed(() => this.lamelareaStore.getLamelareas().filter(l => l.groupId === this.lamelarea().groupId).length === 1);

    ngAfterViewInit() {

        // Start the animation when the component is initialized
        this.cardElement().nativeElement.addEventListener(
            'animationend',
            (event: AnimationEvent) => {
                this.ngZone.runOutsideAngular(() => {
                    this.viewPortComponent()?.start();
                });
            },
            { once: true }
        );
    }

    readonly lamellaRepeatDisabled = computed(() => this.lamelarea().lamellaSets.length === 1);

    readonly availableLamellaAreaSetType: SelectOption[] = [
        { value: "1", label: "INSTALLATION_CONFIGS.MONO" },
        { value: "2", label: "INSTALLATION_CONFIGS.DUO" }
    ];

    removeLamelarea() {
        this.renderingService.stopAnimate();
        this.renderingService.clearViewPort(this.lamelareaId());
        this.cardElement().nativeElement.classList.add('animate-jump-out');
        this.cardElement().nativeElement.addEventListener(
            'animationend',
            (event: AnimationEvent) => {
                // Poista elementti storesta, kun poistoanimaatio päättyy
                this.lamelareaStore.removeLamelarea(this.lamelareaId());
                this.renderingService.startAnimate(true);
            },
            { once: true }
        );
    }

    /**
     * Updates the lamella sets based on the type of the lamella area set.
     * @param $event
     */
    updateLamellaSets($event: any) {
        if($event==null)return;
        this.lamelareaStore.setLamellaAreaLamelInstallationType(this.lamelareaId(), $event.value);
    }

    /**
     * Updates the lamella sets orientation in which way lamellas are going to be installed.
     * @param $event
     */
    orientationChanged($event: string | boolean) {
        if( !(typeof $event === 'boolean') ) { console.error("orientationChanged takes boolean for it to work"); return; }

        const changes: any = {
            //width: this.lamelarea().height,
            //height: this.lamelarea().width,
            orientation: $event ? 'vertical' : 'horizontal',
            automaticFrames: true,
        }

        this.lamelareaStore.updateLamelarea(this.lamelareaId(), changes);
    }

    /**
     * Toggles the mode of the lamelarea assembly configurator; toggle element compares labels and returns current label
     * @param $event
     */
    modeToggle($event: string|boolean) {
        if( typeof $event === 'boolean') { throw new Error("Invalid mode type"); }
        this.lamelareaStore.updateLamelarea(this.lamelarea().id, { automaticFrames: $event === this.modeKey() });
    }

    onMaterialChanged($event: any, type: string) {

        if(type === "coverProfile") {
            const coverProfile = {...this.lamelarea().coverProfile};
            if (coverProfile) {
                //this.viewPortComponent.changeSetColor(coverProfile.color.HEX);
                coverProfile.materialType = $event;
                this.lamelareaStore.updateLamelarea(this.lamelareaId(), {
                    'coverProfile': coverProfile,
                    'syncCoverProfile': false
                });

            }
        }
        else if (type === "mountingProfile"){
            const mountingProfile = {...this.lamelarea().mountingProfile};
            if(!mountingProfile) return;

            mountingProfile.materialType = $event;
            mountingProfile.color = $event === 'Powder Coated' ? ColorConverter.convert("#3F888F") : ColorConverter.convert("#AFAFAF");

            const changes : any = {
                'mountingProfile': mountingProfile,
                'syncMountingProfile': false
            }

            const frameProfile = {...this.lamelarea().frameProfile};
            if (Object.keys(frameProfile).length !== 0) {
                frameProfile.materialType = $event;
                frameProfile.color = $event === 'Powder Coated' ? ColorConverter.convert("#3F888F") : ColorConverter.convert("#AFAFAF");
                changes['frameProfile'] = frameProfile;
            }

            this.lamelareaStore.updateLamelarea(this.lamelareaId(), changes);
        }
    }

    onWidthChange($event: string | number) {

        // Muunnetaan syöte numeroksi, jos se on merkkijonoa
        let width = typeof $event === 'string' ? parseInt($event, 10) : $event;

        // Nopea validointi
        if (width < 1000) width = 1000;
        if (width > 30000) width = 30000;

        this.lamelareaStore.updateLamelarea(this.lamelareaId(), { 'width': width });
    }

    onHeightChange($event: string | number) {

        // Muunnetaan syöte numeroksi, jos se on merkkijonoa
        let height = typeof $event === 'string' ? parseInt($event, 10) : $event;

        // Nopea validointi
        if (height < 1000) height = 1000;
        if (height > 30000) height = 30000;

        this.lamelareaStore.updateLamelarea(this.lamelareaId(), { 'height': height });
    }

    /**
     * Updates the frame profile of the lamelarea based on the selection
     * @param $event {string} - localized value of the selection
     */
    onFrameProfileChanged($event: string) {
        if($event === null) {return;}
        if( !this.frameSelections.find(v=>v === $event ) ) { console.error("selection doesnt direct to any updateStrategy"); return; }

        if($event === "no frame profile") {
            this.lamelareaStore.updateLamelarea(this.lamelareaId(), {'frameProfile': null});
        }
        else {
            const productCode = $event as FrameProfileType === 'frame profile 50x70' ? 'P-17839' : 'P-17061';

            this.lamelareaStore.updateLamelarea(this.lamelareaId(), {'frameProfile': FrameProfileFactory.createProfile({
                    productCode: productCode,
                    materialType: this.lamelarea().mountingProfile.materialType,
                    color: this.lamelarea().mountingProfile.color.HEX,
                    type: $event as FrameProfileType,
            })});
        }
    }

    onColorSelected($event: ColorInfo, selectRef: SelectComponent, type : string) {
        selectRef.close();

        if(type == "coverProfile") {
            const coverProfile = {...this.lamelarea().coverProfile};
            if (coverProfile) {
                //this.viewPortComponent.changeSetColor(coverProfile.color.HEX);
                coverProfile.color = $event;
                this.lamelareaStore.updateLamelarea(this.lamelareaId(), {
                    'coverProfile': coverProfile,
                    'syncCoverProfile': false
                });

            }
        }
        else if (type == "mountingProfile"){
            const mountingProfile = {...this.lamelarea().mountingProfile};
            if (mountingProfile) {
                //this.viewPortComponent.changeSetColor(coverProfile.color.HEX);
                mountingProfile.color = $event;
                this.lamelareaStore.updateLamelarea(this.lamelareaId(), {
                    'mountingProfile': mountingProfile,
                    'syncMountingProfile': false
                });

            }
        }


    }


    /*toggleCoverProfile() {
        if(!this.lamelarea().coverProfile) {
            this.lamelareaStore.updateLamelarea(this.lamelareaId(), {
                'coverProfile': GeneralProfileFactory.createProfile({ materialType: this.lamelarea().lamellaSets[0].material, color: this.lamelarea().lamellaSets[0].color.HEX }),
                'syncCoverProfile': true
            });
        } else {
            this.lamelareaStore.updateLamelarea(this.lamelareaId(), {
                'coverProfile': null,
                'syncCoverProfile': false
            });
        }
    }*/

}
